<template>
  <ReportLayoutModerno
    v-if="prefecture"
    id="motoristasReport"
    :prefecture="prefecture"
  >
    <template v-slot:icon>mdi-card-account-details-outline</template>
    <template v-slot:title>RELATÓRIO DE MOTORISTAS CADASTRADOS</template>

    <div class="motoristas-report-container">
      <!-- CARDS DE RESUMO EXECUTIVO (TOTALIZADORES) -->
      <div class="kpi-grid mb-6">
        <div class="kpi-card">
          <span class="kpi-label">TOTAL MOTORISTAS</span>
          <span class="kpi-value text-slate-800">{{ stats.total }}</span>
        </div>
        <div class="kpi-card">
          <span class="kpi-label">ATIVOS</span>
          <span class="kpi-value text-emerald-600">{{ stats.active }}</span>
        </div>
        <div class="kpi-card">
          <span class="kpi-label">BLOQUEADOS</span>
          <span class="kpi-value text-slate-400">{{ stats.blocked }}</span>
        </div>
        <div class="kpi-card">
          <span class="kpi-label">CNH REGULAR</span>
          <span class="kpi-value text-teal-600">{{ stats.cnhRegular }}</span>
        </div>
        <div class="kpi-card">
          <span class="kpi-label">CNH A VENCER (30 DIAS)</span>
          <span class="kpi-value text-amber-600">{{ stats.cnhExpiring }}</span>
        </div>
        <div class="kpi-card">
          <span class="kpi-label">CNH VENCIDA</span>
          <span class="kpi-value text-rose-600">{{ stats.cnhExpired }}</span>
        </div>
      </div>

      <!-- TABELA EXECUTIVA -->
      <div class="overflow-x-auto">
        <table class="report-table">
          <thead>
            <tr>
              <th style="width: 60px;">CÓDIGO</th>
              <th style="text-align: left;">NOME DO MOTORISTA</th>
              <th style="width: 110px;">CPF</th>
              <th style="width: 105px;">CONTATO</th>
              <th style="width: 65px;">CAT.</th>
              <th style="width: 105px;">REGISTRO CNH</th>
              <th style="width: 95px;">VALIDADE</th>
              <th style="width: 115px;">STATUS CNH</th>
              <th style="width: 85px;">SITUAÇÃO</th>
              <th v-if="showPrefectureColumn" style="text-align: left; width: 140px;">PREFEITURA</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, idx) in data" :key="item.driver_id || idx">
              <td class="text-center font-mono font-bold text-slate-600">
                {{ item.driver_cod || '--' }}
              </td>
              <td class="text-left font-bold text-slate-800 uppercase">
                {{ item.driver_name || 'NÃO INFORMADO' }}
              </td>
              <td class="text-center font-mono text-slate-600">
                {{ item.driver_cpf || '--' }}
              </td>
              <td class="text-center text-slate-600">
                {{ item.driver_cel || item.driver_tel || '--' }}
              </td>
              <td class="text-center font-bold text-slate-800">
                {{ item.driver_category || '--' }}
              </td>
              <td class="text-center font-mono text-slate-600">
                {{ item.driver_cnh_registro || '--' }}
              </td>
              <td class="text-center text-slate-700">
                {{ item.driver_date_validity || '--' }}
              </td>
              <td class="text-center">
                <span :class="getCnhStatusBadgeClass(item.driver_date_validity)" class="badge">
                  {{ getCnhStatusText(item.driver_date_validity) }}
                </span>
              </td>
              <td class="text-center">
                <span :class="item.driver_active ? 'badge-active' : 'badge-inactive'" class="badge">
                  {{ item.driver_active ? 'ATIVO' : 'BLOQUEADO' }}
                </span>
              </td>
              <td v-if="showPrefectureColumn" class="text-left text-[11px] text-slate-600 uppercase">
                {{ item.prefecture_name || '--' }}
              </td>
            </tr>

            <tr v-if="!data || data.length === 0">
              <td :colspan="showPrefectureColumn ? 10 : 9" class="text-center py-8 text-slate-400 font-bold uppercase tracking-widest text-xs">
                Nenhum motorista encontrado para os parâmetros selecionados.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- INFORMAÇÕES DE EMISSÃO -->
      <div class="report-meta mt-8 flex justify-between items-center text-[10px] text-slate-400 border-t border-slate-200 pt-3">
        <span>Relatório emitido em: {{ formattedNow }}</span>
        <span>Total de registros: {{ data.length }}</span>
      </div>
    </div>
  </ReportLayoutModerno>
</template>

<script>
import ReportLayoutModerno from "@/components/ReportLayoutModerno.vue";
import helpers from "@/mixins/helpers";

export default {
  name: "MotoristasReportModel",
  components: {
    ReportLayoutModerno,
  },
  mixins: [helpers],
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    prefecture: {
      type: Object,
      required: true,
    },
    showPrefectureColumn: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    stats() {
      const stats = {
        total: this.data.length,
        active: 0,
        blocked: 0,
        cnhRegular: 0,
        cnhExpiring: 0,
        cnhExpired: 0,
      };

      this.data.forEach((d) => {
        if (d.driver_active) {
          stats.active++;
        } else {
          stats.blocked++;
        }

        const cnhStatus = this.getCnhStatusText(d.driver_date_validity);
        if (cnhStatus === "REGULAR") stats.cnhRegular++;
        else if (cnhStatus === "VENCE EM BREVE") stats.cnhExpiring++;
        else if (cnhStatus === "VENCIDA") stats.cnhExpired++;
      });

      return stats;
    },
    formattedNow() {
      const now = new Date();
      return now.toLocaleDateString("pt-BR") + " às " + now.toLocaleTimeString("pt-BR");
    },
  },
  methods: {
    getCnhStatusText(dateStr) {
      if (!dateStr || dateStr.length !== 10) return "NÃO INFORMADA";
      const parts = dateStr.split("/");
      if (parts.length !== 3) return "INVÁLIDA";
      const validityDate = new Date(parts[2], parts[1] - 1, parts[0]);
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const diffTime = validityDate.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays < 0) return "VENCIDA";
      if (diffDays <= 30) return "VENCE EM BREVE";
      return "REGULAR";
    },
    getCnhStatusBadgeClass(dateStr) {
      const status = this.getCnhStatusText(dateStr);
      if (status === "VENCIDA") return "badge-cnh-expired";
      if (status === "VENCE EM BREVE") return "badge-cnh-expiring";
      if (status === "REGULAR") return "badge-cnh-regular";
      return "badge-cnh-none";
    },
  },
};
</script>

<style scoped>
.motoristas-report-container {
  width: 100%;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 10px;
}

.kpi-card {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 10px;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.kpi-label {
  font-size: 8px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 2px;
}

.kpi-value {
  font-size: 16px;
  font-weight: 900;
  line-height: 1.1;
}

.report-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
}

.report-table thead tr {
  background-color: #333333;
  color: #ffffff;
}

.report-table th {
  padding: 7px 6px;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  border: 1px solid #444444;
  text-align: center;
}

.report-table td {
  padding: 6px 6px;
  border: 1px solid #cbd5e1;
  vertical-align: middle;
}

.report-table tbody tr:nth-child(even) {
  background-color: #f8fafc;
}

.badge {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 9999px;
  font-size: 8.5px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.badge-active {
  background-color: #d1fae5 !important;
  color: #065f46 !important;
  border: 1px solid #a7f3d0;
}

.badge-inactive {
  background-color: #f1f5f9 !important;
  color: #64748b !important;
  border: 1px solid #cbd5e1;
}

.badge-cnh-regular {
  background-color: #ccfbf1 !important;
  color: #115e59 !important;
  border: 1px solid #99f6e4;
}

.badge-cnh-expiring {
  background-color: #fef3c7 !important;
  color: #92400e !important;
  border: 1px solid #fde68a;
}

.badge-cnh-expired {
  background-color: #ffe4e6 !important;
  color: #9f1239 !important;
  border: 1px solid #fecdd3;
}

.badge-cnh-none {
  background-color: #f1f5f9 !important;
  color: #64748b !important;
}

@media print {
  .kpi-grid {
    gap: 6px;
    margin-bottom: 12px !important;
  }
  .kpi-card {
    padding: 4px 6px;
  }
  .kpi-label {
    font-size: 7.5px;
  }
  .kpi-value {
    font-size: 14px;
  }
  .report-table {
    font-size: 10px;
  }
  .report-table th, .report-table td {
    padding: 4px 4px;
  }
  .badge {
    font-size: 8px;
    padding: 1px 4px;
  }
}
</style>
